import { canAccessAdminShell, canImpersonate, canManageRoles } from './adminRoles.js';

/**
 * Admin navigation registry.
 * `reserved: true` items are not rendered and have no mounted route.
 *
 * @typedef {{
 *   id: string,
 *   labelKey: string,
 *   path: string,
 *   icon: string,
 *   allowedRoles: string[],
 *   reserved: boolean,
 *   badgeSource: string | null,
 * }} AdminNavItem
 */

/** @type {AdminNavItem[]} */
export const ADMIN_NAV_ITEMS = [
  {
    id: 'dashboard',
    labelKey: 'admin.nav.dashboard',
    path: '/admin',
    icon: 'LayoutDashboard',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'feedback',
    labelKey: 'admin.nav.feedback',
    path: '/admin/feedback',
    icon: 'MessageSquareWarning',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'approvals',
    labelKey: 'admin.nav.approvals',
    path: '/admin/approvals',
    icon: 'ShieldCheck',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'impersonate',
    labelKey: 'admin.nav.impersonate',
    path: '/admin/impersonate',
    icon: 'UserCheck',
    allowedRoles: ['super_admin', 'admin'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'settings',
    labelKey: 'admin.nav.settings',
    path: '/admin/settings',
    icon: 'Settings',
    allowedRoles: ['super_admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'users',
    labelKey: 'admin.nav.users',
    path: '/admin/users',
    icon: 'Users',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'properties',
    labelKey: 'admin.nav.properties',
    path: '/admin/properties',
    icon: 'Building2',
    allowedRoles: ['super_admin', 'admin', 'support'],
    reserved: false,
    badgeSource: null,
  },
  {
    id: 'activity',
    labelKey: 'admin.nav.activity',
    path: '/admin/activity',
    icon: 'Activity',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'financing',
    labelKey: 'admin.nav.financing',
    path: '/admin/financing',
    icon: 'Banknote',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'market-data',
    labelKey: 'admin.nav.marketData',
    path: '/admin/market-data',
    icon: 'Globe',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'prospection',
    labelKey: 'admin.nav.prospection',
    path: '/admin/prospection',
    icon: 'MapPinned',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
  {
    id: 'audit',
    labelKey: 'admin.nav.audit',
    path: '/admin/audit',
    icon: 'ScrollText',
    allowedRoles: ['super_admin', 'admin'],
    reserved: true,
    badgeSource: null,
  },
];

/**
 * @param {string | null | undefined} role
 * @returns {AdminNavItem[]}
 */
export function visibleAdminNavItems(role) {
  if (!canAccessAdminShell(role)) return [];
  return ADMIN_NAV_ITEMS.filter((item) => {
    if (item.reserved) return false;
    if (item.id === 'impersonate' && !canImpersonate(role)) return false;
    if (item.id === 'settings' && !canManageRoles(role)) return false;
    return item.allowedRoles.includes(role);
  });
}

/**
 * Action Center static metadata (counts come from getAdminDashboardSummary.actions).
 * `countKey` must match ADMIN_DASHBOARD_ACTION_KEYS in functions/admin/dashboard.js.
 * @typedef {{
 *   id: string,
 *   module: string,
 *   titleKey: string,
 *   href: string,
 *   severity: 'info' | 'warning' | 'critical',
 *   allowedRoles: string[],
 *   countKey: 'pendingApprovals' | 'newFeedback',
 * }} AdminActionSource
 */

/** @type {AdminActionSource[]} */
export const ADMIN_ACTION_SOURCES = [
  {
    id: 'pending-approvals',
    module: 'approvals',
    titleKey: 'admin.nav.approvals',
    href: '/admin/approvals',
    severity: 'warning',
    allowedRoles: ['super_admin', 'admin', 'support'],
    countKey: 'pendingApprovals',
  },
  {
    id: 'new-feedback',
    module: 'feedback',
    titleKey: 'admin.nav.feedback',
    href: '/admin/feedback',
    severity: 'info',
    allowedRoles: ['super_admin', 'admin', 'support'],
    countKey: 'newFeedback',
  },
];
